import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ViewChild, ChangeDetectorRef } from '@angular/core';
import { Observable } from 'rxjs';
import { Store, Action } from '@ngrx/store';
import { Router, ActivatedRoute } from '@angular/router';
import { Actions } from '@ngrx/effects';
import { FormArray, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatCheckboxChange } from "@angular/material";
import { CurrentUserService } from '../../security/services/CurrentUserService';
import { ModuleConfiguration } from '../customer.module.config';
import * as fromStore from '../store/reducers';
import * as securityStore from '../../security/store/reducers';
import * as userActions from '../store/actions';
import * as workspaceActions from '../../pillar/workspace/store/actions';
import * as dashboardActions from '../../pillar/dashboard/store/actions';
import * as errorActions from '../../pillar/common/actions/ActionError';
import { AutoSaveFormBuilder } from '../../pillar/workspace/AutoSaveFormBuilder';
import { Subject } from 'rxjs/Subject';
import { AutoSaveFormGroup } from '../../pillar/workspace/AutoSaveFormGroup';
import { UserModel } from '../models/UserModel';
import { UserGroupModel } from '../models/UserGroupModel';
import { UserNameModel } from '../models/UserNameModel';
import { UserAccountDetailedStatusModel } from '../models/UserAccountDetailedStatusModel';
import { LookupModel } from '../../common/models/LookupModel';
import { ManageRolesDialogComponent } from '../../security/components/manage-roles-dialog.component';
import { ManageRolesContext } from '../../security/models/ManageRolesContext';
import { RoleModel } from '../../security/models/RoleModel';
import { AlertsComponent } from '../../pillar/alerts/components/alerts.component';
import { ActionBase } from '../../core/store/action/ActionBase';
import { AppConfigurationService } from '../../core/services/AppConfigurationService';
import { forEach } from '@angular/router/src/utils/collection';
import { NotificationModel } from '../models/NotificationModel';
import { GetCurrentUserSubmittingEntitiesAction } from '../../security/store/actions';
import { UserService } from '../services/UserService';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'brd-user-workspace',
  templateUrl: './user-workspace.component.html',
  styleUrls: ['./user-workspace.component.scss']
})
export class UserWorkspaceComponent implements OnInit, OnDestroy {
  user: UserModel;
  userStatus: UserAccountDetailedStatusModel;
  key: string;
  loading$: Observable<boolean>;
  accountActionLoading: boolean;
  errors$: Observable<string[]>;
  assignedRoles$: Observable<RoleModel[]>;
  destroyed$: Subject<boolean> = new Subject<boolean>();
  formArrayInitMethods = new Map<string, () => void>();
  groupLookups$: Observable<LookupModel[]>;
  notificationTypes$: Observable<LookupModel[]>;
  submittingEntities$: Observable<LookupModel[]>;
  userCurrentStatus$: Observable<UserAccountDetailedStatusModel>;
  profileForm: FormGroup;
  groupsForm: FormGroup;
  notificationsForm: FormGroup;
  rolesForm: FormGroup;
  actionIdentifiers: string[] = [];
  isDelegatingAccountManagementToOtherSystem: boolean;
  groupLookups: LookupModel[] = [];
  hasUniversalGroupAccess:boolean = false;
  isPortal: boolean = false; 
  allUserAccountActionsAreDisplayed: boolean = true;
  title: string;

  editPermission: string = 'CustomerModule.Users:Edit';
  notificationsPermission: string = 'CustomerModule.Users.Notifications:Edit';
  accessAllGroupsPermission: string = 'CustomerModule.Users:AccessGroups';
  lockPermission: string = 'CustomerModule.Users:Lock';
  unlockPermission: string = 'CustomerModule.Users:Unlock';
  disablePermission: string = 'CustomerModule.Users:Disable';
  enablePermission: string = 'CustomerModule.Users:Enable';
  resetPermission: string = 'CustomerModule.Users:ResetPassword';
  editEmailPermission: string = 'CustomerModule.Users:EditEmail';

  @ViewChild(AlertsComponent)
  alerts: AlertsComponent;

  constructor(private store: Store<any>,
    private appConfigurationService: AppConfigurationService,
    private router: Router,
    private formBuilder: AutoSaveFormBuilder,
    private actions$: Actions,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private changeDetectorRef: ChangeDetectorRef,
    private userService: UserService,
    private currentUserService: CurrentUserService) {
    this.isDelegatingAccountManagementToOtherSystem = appConfigurationService.configuration.securityServer.isDelegatingAccountManagementToOtherSystem;
    this.submittingEntities$ = this.store.select(fromStore.getUserSubmittingEntities);
    this.allUserAccountActionsAreDisplayed = appConfigurationService.configuration.allUserAccountActionsAreDisplayed;
    this.title = appConfigurationService.configuration.title;
  }

  storeDispatch(action: Action) {
    var actionBase = action as ActionBase;
    if (actionBase) {
      this.actionIdentifiers.push(actionBase.identifier);
    }
    this.store.dispatch(action);
  }

  ngOnDestroy() {
    this.storeDispatch(new dashboardActions.ClearSelectionAction(ModuleConfiguration.moduleName, UserModel));
    this.destroyed$.next(true);
  }

  refreshUser() {
    if (this.key) {
      this.storeDispatch(new dashboardActions.GetSelectionAction(ModuleConfiguration.moduleName, UserModel, { key: this.key }));
    }
  }

  ngOnInit() {
    this.loading$ = this.store.select(fromStore.getUserListLoading);
    this.errors$ = this.store.select(fromStore.getUserListErrors);
    this.assignedRoles$ = this.store.select(fromStore.getUserAssignedRoles);
    this.store.select(fromStore.getUserGroupLookups).subscribe(result => {
      this.groupLookups = result;
    });

    this.route.params.take(1).subscribe(p => {
      this.key = p['key'];
      this.refreshUser();
    });

    this.route.queryParams.subscribe(qp => {
      var isPortalValue = qp['isPortal'];
      if (isPortalValue) {
        this.isPortal = isPortalValue;
        this.pollPostHeightToIFrameParent();
      }
    });

    this.actions$.ofType(errorActions.ACTION_ERROR)
      .takeUntil(this.destroyed$)
      .filter((a: errorActions.ActionError) => this.actionIdentifiers.includes(a.payload.action.identifier))
      .subscribe((a: errorActions.ActionError) => {
        a.payload.errors.forEach(e => this.alerts.addError(e));
      });


    this.actions$.ofType(userActions.RESET_PASSWORD_SUCCESS)
      .takeUntil(this.destroyed$)
      .subscribe(() => {
        this.accountActionLoading = false;
        this.alerts.addSuccess('Reset Password Success');
        this.changeDetectorRef.markForCheck();
      });

    this.actions$.ofType(userActions.LOCK_ACCOUNT_SUCCESS)  
      .takeUntil(this.destroyed$)
      .subscribe(() => {
        this.accountActionLoading = false;
        this.alerts.addSuccess('Lock Account Success');
        this.changeDetectorRef.markForCheck();
      });

    this.actions$.ofType(userActions.UNLOCK_ACCOUNT_SUCCESS)
      .takeUntil(this.destroyed$)
      .subscribe(() => {
        this.accountActionLoading = false;
        this.alerts.addSuccess('Unlock Account Success');
        this.changeDetectorRef.markForCheck();
      });

    this.actions$.ofType(userActions.DISABLE_ACCOUNT_SUCCESS)
      .takeUntil(this.destroyed$)
      .subscribe(() => {
        this.accountActionLoading = false;
        this.alerts.addSuccess('Disable Account Success');
        this.changeDetectorRef.markForCheck();
      });

    this.actions$.ofType(userActions.ENABLE_ACCOUNT_SUCCESS)
      .takeUntil(this.destroyed$)
      .subscribe(() => {
        this.accountActionLoading = false;
        this.alerts.addSuccess('Enable Account Success');
        this.changeDetectorRef.markForCheck();
      });

    var updateTypes = [
      workspaceActions.updateModelType(ModuleConfiguration.moduleName, UserModel, UserNameModel),
      workspaceActions.updateModelType(ModuleConfiguration.moduleName, UserModel, 'emailAddress'),
      workspaceActions.updateModelType(ModuleConfiguration.moduleName, UserModel, 'groups'),
      workspaceActions.updateModelType(ModuleConfiguration.moduleName, UserModel, NotificationModel)
    ];
    this.actions$
      .takeUntil(this.destroyed$)
      .filter(a => Boolean(updateTypes.find(t => a.type === workspaceActions.updateModelSuccessTypeByUpdateType(t))))
      .filter((a: workspaceActions.UpdateModelSuccessAction<UserModel, any>) => a.payload.key === this.key)
      .subscribe((a: workspaceActions.UpdateModelSuccessAction<UserModel, any>) => {
        a.payload.control.saveSuccess(a.payload.model);
        if (a.type === workspaceActions.updateModelSuccessType(ModuleConfiguration.moduleName, UserModel, 'groups')) {
          a.payload.control.disable();
          this.refreshUser();
        }
        if (a.type === workspaceActions.updateModelSuccessType(ModuleConfiguration.moduleName, UserModel, NotificationModel)) {
          a.payload.control.disable();
        }
      });
    this.actions$
      .takeUntil(this.destroyed$)
      .filter(a => Boolean(updateTypes.find(t => a.type === workspaceActions.updateModelErrorTypeByUpdateType(t))))
      .filter((a: workspaceActions.UpdateModelErrorAction<UserModel, any>) => a.payload.action.payload.key === this.key)
      .subscribe((a: workspaceActions.UpdateModelErrorAction<UserModel, any>) => {
        a.payload.action.payload.control.saveErrors(a.payload.action.payload.model, a.payload.errors);
      });
    var removeGroupType = workspaceActions.removeModelSuccessType(ModuleConfiguration.moduleName, UserModel, 'groups');
    this.actions$.ofType(removeGroupType)
      .takeUntil(this.destroyed$)
      .map((a: any) => a.payload)
      .filter(payload => payload.key === this.key)
      .subscribe((payload) => {
        payload.control.saveSuccess(payload.model);
        var formArray = (this.groupsForm.controls['groups'] as FormArray);
        var index = formArray.controls.indexOf(payload.control);
        formArray.removeAt(index);
        this.refreshUser();
      });
    var removeNotificationType = workspaceActions.removeModelSuccessType(ModuleConfiguration.moduleName, UserModel, NotificationModel);
    this.actions$.ofType(removeNotificationType)
      .takeUntil(this.destroyed$)
      .map((a: any) => a.payload)
      .filter(payload => payload.key === this.key)
      .subscribe((payload) => {
        payload.control.saveSuccess(payload.model);
        var formArray = (this.notificationsForm.controls['notifications'] as FormArray);
        var index = formArray.controls.indexOf(payload.control);
        formArray.removeAt(index);
      });

    this.store
      .select(fromStore.getUserSelection)
      .takeUntil(this.destroyed$)
      .subscribe((v: UserModel) => {
        if (v) {
          this.user = v;
          if (!this.profileForm) {
            this.initForms();
          } else {
            this.updateNotifications();
          }
          this.changeDetectorRef.markForCheck();
          this.store.dispatch(new userActions.GetNotificationTypesAction());
          this.storeDispatch(new userActions.GetUserSubmittingEntitiesAction({ userKey: this.key }));
          this.storeDispatch(new userActions.GetUserCurrentStatusAction({ userKey: this.user.key }));
        }
      });

    this.store
      .select(fromStore.getUserCurrentStatus)
      .takeUntil(this.destroyed$)
      .subscribe((v: UserAccountDetailedStatusModel) => {
        if (v) {
          this.userStatus = v;
          this.changeDetectorRef.markForCheck();
        }
      });

    this.storeDispatch(new dashboardActions.QueryListAction<UserGroupModel>(ModuleConfiguration.moduleName,
      UserGroupModel,
      {
        page: 0,
        pageSize: 1000
      }));
    this.groupLookups$ = this.store.select(fromStore.getUserGroupLookups);
    this.notificationTypes$ = this.store.select(fromStore.getUserNotificationTypes);
    this.submittingEntities$ = this.store.select(fromStore.getUserSubmittingEntities);
    this.userCurrentStatus$ = this.store.select(fromStore.getUserCurrentStatus);
  }

  updateNotifications() {
    var formArray = (this.notificationsForm.controls['notifications'] as FormArray);
    var indexesToRemove = [];
    formArray.controls.forEach(control => {
      var notification = this.user.notifications.find(n => control.value.type.key === n.type.key && control.value.submittingEntity.key === n.submittingEntity.key);
      if (!notification) {
        indexesToRemove.push(formArray.controls.indexOf(control));
      }
    });
    indexesToRemove.forEach(i => formArray.removeAt(i));
  }

  initForms(): void {
    this.profileForm = this.formBuilder.group({
      key: [''],
      name: this.formBuilder.group({
        firstName: ['', Validators.required],
        lastName: ['', Validators.required]
      },
        null,
        this.destroyed$,
        (control) => {
          this.storeDispatch(new workspaceActions.UpdateModelAction(ModuleConfiguration.moduleName, UserModel, UserNameModel, { key: this.key, model: control.value, control: control }));
        },
        this.changeDetectorRef),
      emailAddress: this.formBuilder.control('',
        Validators.email,
        null,
        this.destroyed$,
        (control) => {
          this.storeDispatch(new workspaceActions.UpdateModelAction(ModuleConfiguration.moduleName, UserModel, 'emailAddress', { key: this.key, model: control.value, control: control }));
        },
        this.changeDetectorRef)
    });
    this.groupsForm = this.formBuilder.group({
      groups: this.formBuilder.array(this.user.groups.map(() => this.initGroup(true)))
    });
    this.notificationsForm = this.formBuilder.group({
      notifications: this.formBuilder.array(this.user.notifications.map(() => this.initNotification(true)))
    });

    this.formArrayInitMethods.set('groups', () => this.initGroup());
    this.formArrayInitMethods.set('notifications', () => this.initNotification());

    this.profileForm.patchValue(this.user);
    this.profileForm.markAsPristine();
    this.groupsForm.patchValue(this.user);
    this.groupsForm.markAsPristine();
    this.notificationsForm.patchValue(this.user);
    this.notificationsForm.markAsPristine();
    this.hasUniversalGroupAccess = this.user.hasUniversalGroupAccess
  }

  addFormGroup(form, name) {
    form.controls[name].push(this.formArrayInitMethods.get(name)());
  }

  initGroup(disable?: boolean) {
    return this.formBuilder.group({
      group: [{ value: null, disabled: disable }, Validators.required]
    },
      null,
      this.destroyed$,
      (control) => {
        if (control.value.group.key) {
          this.storeDispatch(new workspaceActions.UpdateModelAction(ModuleConfiguration.moduleName, UserModel, 'groups', { key: this.key, model: control.value.group, control: control }));
        } else {
          return false;
        }
      },
      this.changeDetectorRef);
  }

  initNotification(disable?: boolean) {
    return this.formBuilder.group({
        type: [{ value: null, disabled: disable }, Validators.required],
        submittingEntity: [{ value: null, disabled: disable }, Validators.required]
      },
      null,
      this.destroyed$,
      (control) => {
        if (control.controls["type"].value && control.controls["submittingEntity"].value) {
        this.storeDispatch(new workspaceActions.UpdateModelAction(ModuleConfiguration.moduleName, UserModel, NotificationModel, { key: this.key, model: control.value, control: control }));
        }
      },
      this.changeDetectorRef);
  }

  removeGroup(form, control: AutoSaveFormGroup, index) {
    control.initializeRemove();
    this.storeDispatch(new workspaceActions.RemoveModelAction(ModuleConfiguration.moduleName, UserModel, 'groups', { key: this.key, model: control.value.group, control: control }));
  }

  removeNotification(form, control: AutoSaveFormGroup, index) {
    control.initializeRemove();
    this.storeDispatch(new workspaceActions.RemoveModelAction(ModuleConfiguration.moduleName, UserModel, NotificationModel, { key: this.key, model: control.value, control: control }));
  }

  lookupDisplay(lookup?: LookupModel): string | undefined {
    return lookup ? lookup.description : undefined;
  }

  showManageRoles(): void {
    
    if(this.isDelegatingAccountManagementToOtherSystem) {
      var url = "portal/manage-roles/" + this.key;
      this.postModalUrlToIframeParent(url);
    }else{
      
      var context: ManageRolesContext = {
        assignedRoles$: this.assignedRoles$,
        addRole: (role) => {
          this.storeDispatch(new userActions.AddRoleToUserAction({ userKey: this.user.key, role: role }));
        },
        removeRole: (role) => {
          this.storeDispatch(new userActions.RemoveRoleFromUserAction({ userKey: this.user.key, role: role }));
        },
        loadIncludedRoles: (role) => { },
        accountName: this.user.identifier
      };

      this.dialog.open(ManageRolesDialogComponent,
        {
          panelClass: 'customized-dialog',
          height: '80vh',
          width: '80vw',
          data: context
        });
    } 
  }

  resetPassword() {
    this.accountActionLoading = true;
    this.storeDispatch(new userActions.ResetPasswordAction({ user: this.user }));
  }

  lockAccount() {
    this.accountActionLoading = true;
    this.storeDispatch(new userActions.LockAccountAction({ user: this.user }));
  }

  unlockAccount() {
    this.accountActionLoading = true;
    this.storeDispatch(new userActions.UnlockAccountAction({ user: this.user }));
  }

  disableAccount() {
    this.accountActionLoading = true;
    this.storeDispatch(new userActions.DisableAccountAction({ user: this.user }));
  }

  enableAccount() {
    this.accountActionLoading = true;
    this.storeDispatch(new userActions.EnableAccountAction({ user: this.user }));
  }

  back() {
    this.router.navigateByUrl('/administration');
  }
  canEditEmail() {
    return this.currentUserService.hasPermission(this.editEmailPermission);
  }

  canEditAccessGroups() {
    return this.currentUserService.hasPermission(this.accessAllGroupsPermission);
  }

  checkChanged(event: MatCheckboxChange, data: LookupModel) {          
        this.storeDispatch(new userActions.AddAllGroupsToUserAction({ userKey: this.user.key, allowAllGroups:event.checked, lookUpModels:this.groupLookups }));
        this.submittingEntities$ = this.store.select(fromStore.getUserSubmittingEntities);
  }

  pollPostHeightToIFrameParent(height: number = undefined) {
    window.setTimeout(() => {
        var variableHeightElement = document.querySelector("brd-user-workspace");
        if (variableHeightElement) {
          var currentHeight = variableHeightElement.clientHeight;
          if (height != currentHeight) {
            height = currentHeight;
            this.postHeightToIframeParent(height);
          }
        }
        this.pollPostHeightToIFrameParent(height);
      },
      200);
  }

  postHeightToIframeParent(height: number) {
    window.parent.postMessage({ "height": height, }, "*");
  }

  postModalUrlToIframeParent(url: string){
    window.parent.postMessage({"modalUrl": url}, "*");
  }
}
 


// WEBPACK FOOTER //
// ./src/app/customer/components/user-workspace.component.ts